// Claude: New component for specification.md Phase 3 (DB optimiser/cleaner) — approver/admin-facing
// Claude: duplicate/conflict review UI (backend: /v1/duplicate-queue/, /v1/resolve-duplicate/).
import React, { useState, useEffect, useContext } from "react";
import Modal from "react-modal";
import languageMapper, { baseUrl, getValidAccessToken } from "../shared";
import Logo from "../logo512.png";
import Navbar from "../navbar";
import Navbaranonuser from "../navbaranonuser";
import { AuthContext } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../i18n";

// Claude: new for UAT feedback — "see both word's full entries side-to-side before making a
// Claude: decision... clicking on [the English word] will display A and B in a pop-up." The full
// Claude: Word record for both sides is already in the API response (WordSerializer's usual
// Claude: fields — duplicate_queue's table only ever showed `word`/`status`), so this is purely a
// Claude: frontend addition, no backend change needed. One row per field keeps A and B easy to
// Claude: compare at a glance rather than two separate blocks of prose.
const DETAIL_FIELDS = [
  { key: "status", labelKey: "status" },
  { key: "pos", labelKey: "pos" },
  { key: "definition", labelKey: "definition" },
  { key: "example", labelKey: "example" },
  { key: "attribution", labelKey: "attribution" },
  { key: "field", labelKey: "context" },
  { key: "school", labelKey: "school" },
  { key: "synonyms", labelKey: "synonyms" },
  { key: "editor", labelKey: "editor" },
  { key: "date_edited", labelKey: "date_edited_label" },
  // Claude: notes field (alpha-tester request) — shown so a reviewer can compare them; a merge keeps
  // Claude: both notes on the surviving entry (see _merged_notes in views.py).
  { key: "notes", labelKey: "notes" },
];

// Claude: new for UAT feedback — "I do not see a fuzzy comparison option -- please suggest a way
// Claude: to set the 'fuzziness' of the search." Matches FUZZY_DUPLICATE_THRESHOLD's default in
// Claude: views.py (0.8 — a difflib.SequenceMatcher ratio, not a percent, but shown as one here
// Claude: since "80% similar" reads more intuitively than "0.8" for a non-technical reviewer).
const DEFAULT_FUZZINESS = 0.8;

// Claude: new for UAT feedback — "extend duplicate testing to include other statuses as well
// Claude: (selectable)... test pending words against existing entries, or multiple pending words
// Claude: vying for a position." Checking PND alongside ACC surfaces "pending vs already-accepted"
// Claude: pairs; PND alone surfaces "multiple pending words competing for the same slot" — both
// Claude: are just this same checkbox set feeding views.py's ?statuses= filter differently.
// Claude: 'ACC' alone (the original scope) stays the default so existing usage is unaffected.
const STATUS_OPTIONS = [
  { code: "ACC", labelKey: "accepted" },
  { code: "PND", labelKey: "pending" },
  { code: "REJ", labelKey: "rejected" },
  { code: "FLG", labelKey: "flagged" },
];
const DEFAULT_STATUSES = ["ACC"];
const DUPLICATE_PAGE_SIZE = 50;

const DuplicateCleanup = () => {
  const [duplicatePairs, setDuplicatePairs] = useState([]);
  const [totalPairs, setTotalPairs] = useState(0);
  const [exactPairs, setExactPairs] = useState(0);
  const [offset, setOffset] = useState(0);
  const [fuzziness, setFuzziness] = useState(DEFAULT_FUZZINESS);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [error, setError] = useState(null);
  // Claude: separate from isLoading below — this is specifically the bulk-merge request itself,
  // Claude: shown on the bulk-merge button rather than swapping out the whole table.
  const [isMerging, setIsMerging] = useState(false);
  // Claude: bugfix reported during UAT — "the tick mark ... takes a few seconds ... At the very
  // Claude: least, the tick box activation/deactivation should appear immediately, and a spinner
  // Claude: should alert the user that the list will follow." Even with the query itself now much
  // Claude: faster (see the aggregation rewrite below), it's still a real network round-trip, not
  // Claude: instant — the checkbox/slider must reflect the click immediately regardless.
  const [isLoading, setIsLoading] = useState(false);
  // Claude: new for UAT feedback — which pair (if any) is currently shown in the side-by-side
  // Claude: comparison pop-up. Holds the whole pair object (not just an id) so the modal doesn't
  // Claude: need a second lookup/request — duplicate_queue's response already carries full Word
  // Claude: data for both sides.
  const [selectedPair, setSelectedPair] = useState(null);
  const { auth } = useContext(AuthContext);
  const { t } = useTranslation();

  const fetchDuplicatePairs = async ({
    fetchOffset = offset,
    fetchFuzziness = fuzziness,
    fetchStatuses = statuses,
  } = {}) => {
    // Claude: optimistic update — set these from what was requested, not what the response later
    // echoes back, so the checkbox tick / slider position change the instant the user acts.
    setFuzziness(fetchFuzziness);
    setStatuses(fetchStatuses);
    setIsLoading(true);
    const token = await getValidAccessToken();
    try {
      const response = await fetch(
        baseUrl +
          `v1/duplicate-queue/?limit=${DUPLICATE_PAGE_SIZE}&offset=${fetchOffset}` +
          `&fuzziness=${fetchFuzziness}&statuses=${fetchStatuses.join(",")}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP status ${response.status}`);
      }
      const data = await response.json();
      setDuplicatePairs(data.duplicate_pairs);
      setTotalPairs(data.total_pairs);
      setExactPairs(data.exact_pairs);
      setOffset(data.offset);
    } catch (err) {
      console.error("Error:", err);
      // Claude: alpha-tester feedback — "the error message is displayed... but there is no link to
      // Claude: the login page." Same pattern UserRoleManager.js already uses.
      setError(
        <>
          Error fetching the duplicate queue — please{' '}
          <Link to="/login" className="text-blue-500 underline hover:text-blue-700">log in</Link> again.
        </>
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDuplicatePairs({ fetchOffset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFuzzinessChange = (e) => {
    fetchDuplicatePairs({ fetchOffset: 0, fetchFuzziness: Number(e.target.value) });
  };

  const handleStatusToggle = (code) => {
    const nextStatuses = statuses.includes(code)
      ? statuses.filter((s) => s !== code)
      : [...statuses, code];
    if (nextStatuses.length === 0) {
      return; // Claude: at least one status must stay selected — nothing to compare otherwise.
    }
    fetchDuplicatePairs({ fetchOffset: 0, fetchStatuses: nextStatuses });
  };

  const handleResolve = async (action, keepId, discardId) => {
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + "v1/resolve-duplicate/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action, keep_id: keepId, discard_id: discardId }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchDuplicatePairs({ fetchOffset: offset });
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err) {
      alert(t("generic_error"));
    }
  };

  // Claude: new for UAT feedback — "there are cases where a merge is not the appropriate route,
  // Claude: and where deleting one of the duplicates would be a better option." Unlike merge, this
  // Claude: leaves the OTHER word completely untouched — no synonym is added, nothing about the
  // Claude: deleted one is preserved, which is the point: a merge always leaves a trace on the
  // Claude: survivor, and sometimes that trace isn't wanted (e.g. the discarded entry was simply
  // Claude: wrong, not a legitimate alternate spelling). Reuses the existing DELETE
  // Claude: /v1/deleteword/<id> endpoint (already used by PhraseDetail.js's own delete button)
  // Claude: rather than adding a new backend action — deleting a word is already exactly that.
  const handleDelete = async (word, onAfterClick = () => {}) => {
    if (!window.confirm(t("delete_word_confirm", { word: word.word }))) {
      return;
    }
    const token = await getValidAccessToken();
    try {
      await fetch(baseUrl + `v1/deleteword/${word._id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      fetchDuplicatePairs({ fetchOffset: offset });
      onAfterClick();
    } catch (err) {
      alert(t("generic_error"));
    }
  };

  // Claude: shared between the table row and the new comparison pop-up (see selectedPair above) —
  // Claude: same three actions either way. `onAfterClick` lets the pop-up close itself once a
  // Claude: decision is made, since the pair it's showing is about to disappear from the list.
  const renderDecisionButtons = (pair, onAfterClick = () => {}) => {
    const resolve = (action, keepId, discardId) => {
      handleResolve(action, keepId, discardId);
      onAfterClick();
    };
    return (
      <>
        <button
          onClick={() => resolve("merge", pair.word_a._id, pair.word_b._id)}
          className="bg-cyan-700 text-white py-1 px-3 rounded-md text-sm hover:bg-cyan-800"
        >
          {t("keep_a_merge_b")}
        </button>
        {" "}
        <button
          onClick={() => resolve("merge", pair.word_b._id, pair.word_a._id)}
          className="bg-cyan-700 text-white py-1 px-3 rounded-md text-sm hover:bg-cyan-800"
        >
          {t("keep_b_merge_a")}
        </button>
        {" "}
        <button
          onClick={() => resolve("reject", pair.word_a._id, pair.word_b._id)}
          className="bg-gray-700 text-white py-1 px-3 rounded-md text-sm hover:bg-gray-800"
        >
          {t("not_a_duplicate")}
        </button>
      </>
    );
  };

  // Claude: new for UAT feedback — "add an option to merge all exact matches. For exact matches,
  // Claude: it does not matter which one to keep." This is irreversible and, at real-dataset
  // Claude: scale, can affect thousands of entries (confirmed: 11,553 clusters / 14,490 words for
  // Claude: the PND+ACC scope on the real dev dataset) — window.confirm() names the scale from
  // Claude: exactPairs before anything happens, matching how PhraseDetail.js's own delete flow
  // Claude: requires an explicit confirmation before an irreversible action.
  const handleMergeAllExact = async () => {
    if (!window.confirm(t("merge_all_exact_confirm", { count: exactPairs }))) {
      return;
    }
    setIsMerging(true);
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + "v1/merge-all-exact-duplicates/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ statuses: statuses.join(",") }),
      });
      const data = await response.json();
      if (response.ok) {
        alert(t("merge_all_exact_done", { clusters: data.clusters_merged, words: data.words_deleted }));
        await fetchDuplicatePairs({ fetchOffset: 0 });
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err) {
      alert(t("generic_error"));
    } finally {
      setIsMerging(false);
    }
  };

  const hasPrevious = offset > 0;
  const hasNext = offset + DUPLICATE_PAGE_SIZE < totalPairs;

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {auth.userName === null && <Navbaranonuser />}
      {auth.userName != null && <Navbar />}

      <div className="flex flex-col justify-center items-center p-2">
        <h3 className="text-4xl font-bold mb-6">
          <Link to={"/"}>{t("duplicate_cleanup")}</Link>
        </h3>

        <div className="flex flex-wrap items-start gap-6 mb-4">
          {/* Claude: new for UAT feedback — a lower fuzziness surfaces looser/more distant spelling
              matches (more results, more false positives); a higher one only near-identical
              spellings (fewer results, stricter). Applies immediately on change, matching
              ReviewQueue.js's language-filter pattern. */}
          <div className="w-full max-w-xs">
            <label htmlFor="duplicate-fuzziness" className="block text-sm text-gray-700">
              {t("fuzziness_label")} {Math.round(fuzziness * 100)}%
            </label>
            <input
              id="duplicate-fuzziness"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={fuzziness}
              onChange={handleFuzzinessChange}
              className="w-full"
            />
          </div>

          {/* Claude: new for UAT feedback — selectable statuses (see STATUS_OPTIONS above). */}
          <div>
            <span className="block text-sm text-gray-700">{t("statuses_label")}</span>
            {STATUS_OPTIONS.map((option) => (
              <label key={option.code} className="inline-flex items-center mr-4">
                <input
                  type="checkbox"
                  checked={statuses.includes(option.code)}
                  onChange={() => handleStatusToggle(option.code)}
                  className="form-checkbox h-4 w-4 text-cyan-700 accent-cyan-700"
                />
                <span className="ml-1">{t(option.labelKey)}</span>
              </label>
            ))}
          </div>

          {/* Claude: new for UAT feedback — "add an option to merge all exact matches." Only
              shown once there's at least one exact match in the current scope; window.confirm()
              inside handleMergeAllExact names the scale before anything irreversible happens. */}
          {exactPairs > 0 && (
            <div>
              <span className="block text-sm text-gray-700">&nbsp;</span>
              <button
                onClick={handleMergeAllExact}
                disabled={isMerging}
                className="bg-rose-700 text-white py-2 px-4 rounded-lg hover:bg-rose-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMerging ? t("merging") : t("merge_all_exact", { count: exactPairs })}
              </button>
            </div>
          )}
        </div>

        {isLoading ? (
          // Claude: bugfix reported during UAT — a spinner while the (network-bound, not
          // Claude: instant) request is in flight, matching the established loading-indicator
          // Claude: pattern already used in AdvancedSearch.js/LandingPage.js.
          <div className="flex items-center justify-center">
            <div
              className="inline-block mt-6 h-8 w-8 animate-spin rounded-full text-cyan-900 border-4 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
            >
              <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                Loading...
              </span>
            </div>
          </div>
        ) : (
          <>
            {duplicatePairs.length === 0 && <p>{t("no_duplicates_found")}</p>}
            {totalPairs > 0 && (
              <p>
                {t("showing_range", { start: offset + 1, end: offset + duplicatePairs.length, total: totalPairs })}
              </p>
            )}
            <table className="border-separate [border-spacing:0.8rem]">
              <thead>
                <tr>
                  <th>{t("match_type")}</th>
                  <th>{t("english")}</th>
                  <th>{t("language")}</th>
                  <th>{t("word_a")}</th>
                  <th>{t("word_b")}</th>
                  <th>{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {duplicatePairs.map((pair) => (
                  <tr key={`${pair.word_a._id}-${pair.word_b._id}`}>
                    <td>{pair.match_type} ({pair.similarity})</td>
                    <td>
                      {/* Claude: new for UAT feedback — "make the English word active, so that
                          clicking on it will display A and B in a pop-up" (see the Modal below). */}
                      <button
                        type="button"
                        onClick={() => setSelectedPair(pair)}
                        className="text-cyan-700 underline hover:text-cyan-800"
                      >
                        {pair.wordEnglish}
                      </button>
                    </td>
                    {/* Claude: bugfix reported during UAT — this showed the raw 3-letter language
                        code (e.g. "Afr") instead of the full name every other user-facing display
                        in the app already uses (see languageMapper in shared.js). */}
                    <td>{languageMapper(pair.language)}</td>
                    {/* Claude: new for UAT feedback — each word's status is now shown alongside it,
                        since a pair can mix statuses (e.g. PND vs ACC) once more than one status is
                        selected above, and the reviewer needs to see which side is which. */}
                    <td>{pair.word_a.word} ({pair.word_a.status})</td>
                    <td>{pair.word_b.word} ({pair.word_b.status})</td>
                    {/* Claude: bugfix reported during UAT (same issue as ReviewQueue.js) — these
                        were unstyled <button>s that didn't read as buttons at all. */}
                    <td>{renderDecisionButtons(pair)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(hasPrevious || hasNext) && (
              <div className="mt-4">
                <button
                  onClick={() => fetchDuplicatePairs({ fetchOffset: Math.max(0, offset - DUPLICATE_PAGE_SIZE) })}
                  disabled={!hasPrevious}
                  className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("previous")}
                </button>
                {" "}
                <button
                  onClick={() => fetchDuplicatePairs({ fetchOffset: offset + DUPLICATE_PAGE_SIZE })}
                  disabled={!hasNext}
                  className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("next")}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Claude: new for UAT feedback — "see both word's full entries side-to-side before making
          a decision." Modelled on the existing NoResultsModal.js pattern (react-modal, same
          appElement/className convention). */}
      <Modal
        isOpen={selectedPair !== null}
        onRequestClose={() => setSelectedPair(null)}
        appElement={document.getElementById("root")}
        className="modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        {selectedPair && (
          <div className="bg-white rounded-lg shadow-lg w-11/12 md:w-3/4 max-w-4xl max-h-[90vh] overflow-auto p-4">
            <button
              onClick={() => setSelectedPair(null)}
              className="bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600"
            >
              {t("back")}
            </button>
            <h2 className="text-xl font-semibold mt-4 mb-4">
              {selectedPair.wordEnglish} ({languageMapper(selectedPair.language)})
            </h2>
            <table className="w-full border-separate [border-spacing:0.5rem]">
              <thead>
                <tr>
                  <th></th>
                  <th className="text-left">{t("word_a")}: {selectedPair.word_a.word}</th>
                  <th className="text-left">{t("word_b")}: {selectedPair.word_b.word}</th>
                </tr>
              </thead>
              <tbody>
                {DETAIL_FIELDS.map((field) => (
                  <tr key={field.key}>
                    <th className="text-left align-top">{t(field.labelKey)}</th>
                    <td className="align-top">{selectedPair.word_a[field.key] || "—"}</td>
                    <td className="align-top">{selectedPair.word_b[field.key] || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4">
              {renderDecisionButtons(selectedPair, () => setSelectedPair(null))}
              {" "}
              {/* Claude: new for UAT feedback — "there are cases where a merge is not the
                  appropriate route, and where deleting one of the duplicates would be a better
                  option... two red buttons to delete A or B." Only offered in the pop-up (not the
                  compact table row) since it needs the full detail view to judge safely. */}
              <button
                onClick={() => handleDelete(selectedPair.word_a, () => setSelectedPair(null))}
                className="bg-rose-700 text-white py-1 px-3 rounded-md text-sm hover:bg-rose-800"
              >
                {t("delete_a")}
              </button>
              {" "}
              <button
                onClick={() => handleDelete(selectedPair.word_b, () => setSelectedPair(null))}
                className="bg-rose-700 text-white py-1 px-3 rounded-md text-sm hover:bg-rose-800"
              >
                {t("delete_b")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <div className="flex flex-col justify-center items-center p-4">
        <Link to={"/"}>
          <img src={Logo} alt="Logo" width="100" height="100" />
        </Link>
      </div>
    </div>
  );
};

export default DuplicateCleanup;
